import { Button, Card, Divider, Stack, Typography } from "@mui/material";
import { CampaignContext } from "../../Context";
import { useContext, useEffect, useState } from "react";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import { cloneDeep } from "lodash";
import FromSection from "../../components/FromSection";
import RecipientsSection from "../../components/RecipientSection/RecipientsSection";
import ChildCampaigns from "./ChildCampaigns";
import { SubmitCampaign } from "types/Campaign";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import {
  fetchApprovedDomains,
  runSequentialCampaigns,
} from "screens/Campaigns/CampaignService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

interface CustomProps {
  activeStep: number;
  setActiveStep: Function;
  setChildCampaigns: Function;
  childCampaigns: SubmitCampaign[];
}

const Step1 = (props: CustomProps) => {
  const { submitCampaign, setSubmitCampaign } = useContext(CampaignContext);
  const [submittingCampaign, setSubmittingCampaign] = useState<boolean>(false);

  useEffect(() => {
    if (!isTruthy(submitCampaign.campaignData.domainName)) {
      updateDomainDetails();
    }
  }, []);

  useEffect(() => {
    if (props.activeStep === 0) {
      const clonedChildCampaigns = cloneDeep(props.childCampaigns);
      const updatedChildCampaigns = clonedChildCampaigns.map((child) => {
        return {
          ...child,
          campaign: {
            ...child.campaign,
            fromId: submitCampaign.campaign.fromId,
            displayName: submitCampaign.campaign.displayName,
          },
          campaignSegments: {
            ...child.campaignSegments,
            segmentTypeId: submitCampaign.campaignSegments.segmentTypeId,
            segmentIds: submitCampaign.campaignSegments.segmentIds,
            recipients: submitCampaign.campaignSegments.recipients,
          },
          campaignData: {
            ...child.campaignData,
            domainName: submitCampaign.campaignData.domainName,
          },
        };
      });
      props.setChildCampaigns(updatedChildCampaigns);
    }
  }, [submitCampaign.campaignSegments, submitCampaign.campaignData]);

  const validateSequentialCampaign = () => {
    const hasError = props.childCampaigns.map((child) => {
      return (
        child.campaignSegments.segmentIds.length === 0 ||
        !child.campaign.fromIdEmail ||
        !child.campaignData.domainName ||
        !child.campaign.subject ||
        !child.campaign.content ||
        !child.campaign.scheduleTime
      );
    });
    return hasError.some((value) => value === true);
  };

  const updateDomainDetails = async () => {
    try {
      const response = await fetchApprovedDomains();
      if (response.length === 1) {
        setSubmitCampaign({
          ...submitCampaign,
          campaignData: {
            ...submitCampaign.campaignData,
            domainName: response[0],
          },
        });
      }
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const submitSequentialCampaigns = async () => {
    try {
      setSubmittingCampaign(true);
      let clonedChildCampaigns = cloneDeep(props.childCampaigns);
      clonedChildCampaigns = clonedChildCampaigns.map((child) => {
        child.campaign.id = 0;
        return child;
      });
      await runSequentialCampaigns(clonedChildCampaigns);
      openSuccessNotification("Sequential campaign submitted successfully");
      history.push(
        `${
          viewpaths.campaignsViewPath
        }?page=${1}&rowsPerPage=${10}&status=Submitted&category=all&currentTab=email`
      );
      setSubmittingCampaign(false);
    } catch (error: any) {
      setSubmittingCampaign(false);
      handleErrorMessage(error);
    }
  };

  const getPage = () => {
    return (
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant="h4">Create sequences of campaigns</Typography>
          <Typography variant="body1">
            Sequential campaigns allow you to set up multiple campaigns with a
            same from and recipients. Create up to 4 campaigns in parallel.
          </Typography>
        </Stack>
        <Card>
          <Stack spacing={4} py={4} divider={<Divider />}>
            <FromSection />
            <RecipientsSection />
            <ChildCampaigns
              childCampaigns={props.childCampaigns}
              setChildCampaigns={props.setChildCampaigns}
              setActiveStep={props.setActiveStep}
            />
          </Stack>
        </Card>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            variant="text"
            onClick={() =>
              history.push(`${viewpaths.campaignsViewPath}?currentTab=email`)
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitSequentialCampaigns}
            disabled={
              !submitCampaign.campaignData.domainName ||
              validateSequentialCampaign()
            }
          >
            Submit sequential campaign
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      {getPage()}
      <CustomLoader isLoading={submittingCampaign} />
    </>
  );
};

export default Step1;
